import { ActionConfirmationHost } from "../ui/ActionConfirmation.js";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { get, type StudentDetail, type StudentSummary } from "../api.js";
import { isOwner, useAuth } from "../auth.js";
import { useAsync } from "../useAsync.js";
import { BlockSaleWorkflow } from "../booking/BlockSaleWorkflow.js";
import { LessonPaymentPicker } from "../booking/LessonPaymentPicker.js";
import { paymentWorkflowAccess } from "../booking/paymentWorkflow.js";
import { studentRosterLearning } from "./Students.js";

type StudentChoice = Pick<StudentSummary, "id" | "fullName" | "parentName" | "instrument" | "enrolments">;

/** Enough to tell two students with the same name apart before taking money. */
export function studentChoiceDetails(student: StudentChoice): string {
  return [...studentRosterLearning(student), ...(student.parentName ? [`Parent: ${student.parentName}`] : [])].join(" · ");
}

export function LogPayment() {
  return <ActionConfirmationHost><LogPaymentContent /></ActionConfirmationHost>;
}

function LogPaymentContent() {
  const { me } = useAuth();
  const access = paymentWorkflowAccess(me);
  const [params, setParams] = useSearchParams();
  if (!access.lessons && !access.blocks) return <><h1>Log payment</h1><p>You do not have access to payment logging.</p></>;
  const studentId = params.get("student");
  return <div className="payments-workspace">
    <h1>Log payment</h1>
    <p>Record a payment already received for a lesson or lesson block.</p>
    {studentId ? <SelectedStudent key={studentId} studentId={studentId} onBack={() => setParams({})} />
      : <PaymentStudentSearch ownOnly={!isOwner(me) && !access.manageAll} onSelect={(student) => setParams({ student: student.id })} />}
  </div>;
}

export function PaymentStudentSearch({ ownOnly, onSelect }: { ownOnly: boolean; onSelect: (student: StudentChoice) => void }) {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<StudentChoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    setRows([]);
    setError(null);
    setLoading(query.trim() !== "");
    if (!query.trim()) return;
    const timer = window.setTimeout(() => {
      void get<{ students: StudentChoice[] }>("/admin/students", {
        q: query.trim(), limit: "20", ...(ownOnly ? { scope: "mine" } : {}),
      }).then((data) => { if (current) setRows(data.students); })
        .catch((cause: unknown) => { if (current) setError(cause instanceof Error ? cause.message : "Student search failed."); })
        .finally(() => { if (current) setLoading(false); });
    }, 250);
    return () => { current = false; window.clearTimeout(timer); };
  }, [query, ownOnly]);
  return <section>
    <label className="stack">Student search<input type="search" value={query} maxLength={100} autoComplete="off"
      placeholder="Start typing a student’s name" onChange={(event) => {
        setRows([]);
        setQuery(event.target.value);
      }} /></label>
    {loading && <p role="status">Searching students…</p>}
    {error && <p role="alert">{error}</p>}
    {!loading && !error && query.trim() && rows.length === 0 && <p>No matching students.</p>}
    <ul aria-label="Matching students">{rows.map((student) => <li key={student.id}>
      <button className="quiet" onClick={() => onSelect(student)}>
        <strong>{student.fullName}</strong> <span className="small muted">{studentChoiceDetails(student)}</span>
      </button>
    </li>)}</ul>
    {rows.length === 20 && <p>Showing the first 20 matches. Keep typing to narrow your search.</p>}
  </section>;
}

function SelectedStudent({ studentId, onBack }: { studentId: string; onBack: () => void }) {
  const { me } = useAuth();
  const access = paymentWorkflowAccess(me);
  const student = useAsync(() => get<StudentDetail>(`/admin/students/${encodeURIComponent(studentId)}`), [studentId]);
  const [kind, setKind] = useState<"lesson" | "block" | null>(null);
  function choose(next: "lesson" | "block") {
    setKind(next);
  }
  if (student.loading) return <p role="status">Loading student…</p>;
  if (student.error) return <><p role="alert">{student.error}</p><button onClick={onBack}>Choose another student</button></>;
  if (!student.data) return null;
  if (kind === "lesson" && !access.lessons) {
    return <p>You no longer have access to lesson payment logging.</p>;
  }
  return <>
    <h2>{student.data.student.fullName}</h2>
    {kind === null ? <>
      <p>What are they paying for?</p>
      <div className="row">
        {access.lessons && <button onClick={() => choose("lesson")}>Lesson</button>}
        {access.blocks && <button onClick={() => choose("block")}>Lesson block</button>}
        <button className="quiet" onClick={onBack}>Choose another student</button>
      </div>
      <p><Link to={`/students/${encodeURIComponent(studentId)}`}>Open student profile</Link></p>
    </> : kind === "lesson" ? <>
      <LessonPaymentPicker studentId={studentId} onCancel={() => setKind(null)} />
    </> : <BlockSaleWorkflow studentId={studentId} studentName={student.data.student.fullName}
      onCancel={() => setKind(null)} onChanged={() => {
        setKind(null);
      }} />}
  </>;
}
